'use client'
import React from 'react'

type StepKey = 'cart' | 'shipping' | 'payment'

interface Props {
    step?: StepKey
}

export default function CartSkeleton({ step = 'cart' }: Props) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
            <div className="lg:col-span-2">
                <div className="space-y-4 p-4">
                    {step === 'cart' && <CartListSkeleton />}
                    {step === 'shipping' && <ShippingFormSkeleton />}
                    {step === 'payment' && <PaymentSkeleton />}
                </div>
            </div>

            <div className="lg:col-span-1">
                <div className="sticky top-18 p-4">
                    <OrderSummarySkeleton />
                </div>
            </div>
        </div>
    )
}

/* ---------- cart skeleton ---------- */
export function CartListSkeleton() {
    // نمایش 3 آیتم شبیه CartItem
    return (
        <>
            <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-xs animate-pulse"
                    >
                        <div className="flex-shrink-0 relative aspect-square min-h-32 bg-gray-200 rounded" />

                        <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex flex-col space-y-3 w-full">
                                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                                </div>

                                <div className="w-8 h-8 bg-gray-200 rounded-full ml-4" />
                            </div>

                            <div className="border-t-1 border-gray-250 w-full my-3" />

                            <div className="flex justify-between items-center">
                                <div className="flex gap-3 items-center">
                                    <div className="flex items-center gap-3 bg-white rounded-xs p-2">
                                        <div className="w-7 h-7 bg-gray-200 rounded-full" />
                                        <div className="font-primary-bold min-w-[2rem] text-center"> </div>
                                        <div className="w-7 h-7 bg-gray-200 rounded-full" />
                                    </div>

                                    <div className="space-y-1">
                                        <div className="h-4 bg-gray-200 rounded w-20" />
                                        <div className="h-3 bg-gray-200 rounded w-12" />
                                    </div>
                                </div>

                                <div className="text-end space-y-1">
                                    <div className="h-5 bg-gray-200 rounded w-20" />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-4 flex justify-between items-center">
                <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
                <div className="w-40 h-10 bg-gray-200 rounded animate-pulse" />
            </div>
        </>
    )
}

/* ---------- payment skeleton ---------- */
export function PaymentSkeleton() {
    return (
        <div className="space-y-6">
            {/* coupon box */}
            <div className="bg-gray-50 rounded-xs p-4 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-3" />
                <div className="h-10 bg-gray-200 rounded w-full" />
            </div>

            {/* payment method cards */}
            <div className="bg-gray-50 rounded-xs p-4 space-y-4 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-sm border-2 border-gray-200">
                        <div className="h-6 bg-gray-200 rounded w-1/2 mb-3" />
                        <div className="h-10 bg-gray-200 rounded" />
                    </div>
                    <div className="p-4 rounded-sm border-2 border-gray-200">
                        <div className="h-6 bg-gray-200 rounded w-1/2 mb-3" />
                        <div className="h-10 bg-gray-200 rounded" />
                    </div>
                </div>
            </div>
        </div>
    )
}

/* ---------- shipping skeleton ---------- */
export function ShippingFormSkeleton() {
    return (
        <div className="space-y-6">
            <div className="p-4 border rounded space-y-4 animate-pulse bg-gray-50">
                <div className="h-6 bg-gray-200 rounded w-1/3" />
                <div className="grid grid-cols-2 gap-4">
                    <div className="h-10 bg-gray-200 rounded" />
                    <div className="h-10 bg-gray-200 rounded" />
                    <div className="h-10 bg-gray-200 rounded col-span-2" />
                    <div className="h-10 bg-gray-200 rounded" />
                    <div className="h-10 bg-gray-200 rounded" />
                </div>
            </div>

            <div className="p-4 border rounded animate-pulse bg-gray-50">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                <div className="h-10 bg-gray-200 rounded" />
            </div>

            {/* map placeholder */}
            <div className="p-4 border rounded animate-pulse bg-gray-50">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-3" />
                <div className="w-full h-40 bg-gray-200 rounded" />
            </div>
        </div>
    )
}

/* ---------- order summary skeleton (right side) ---------- */
function OrderSummarySkeleton() {
    return (
        <div className="p-4 border rounded animate-pulse bg-white">
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-4 bg-gray-200 rounded w-16" />
                </div>
                <div className="flex justify-between items-center">
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-4 bg-gray-200 rounded w-16" />
                </div>
                <div className="flex justify-between items-center">
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-4 bg-gray-200 rounded w-16" />
                </div>

                <div className="h-10 bg-gray-200 rounded w-full mt-4" />
                <div className="h-10 bg-gray-200 rounded w-full mt-2" />
            </div>
        </div>
    )
}
